// Hook centralisant l'état de la collection utilisateur :
// - la map { "cardId::variant": status }
// - les stats (possédées, recherchées, valeur)
// - la complétion par set
// - les actions add/remove avec mise à jour optimiste
// - étape 3 du mode hors ligne : les écritures qui échouent par coupure
//   réseau (pas un vrai rejet serveur) sont mises en file et rejouées au
//   retour de connexion (cf. lib/offlineQueue.js)

import { useState, useCallback, useEffect, useMemo } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import {
  fetchCollectionCardIds, fetchCollectionStats, fetchCollectionCompletion,
  addToCollection, removeFromCollection, bulkSetStatus, bulkRemoveFromCollection,
  fetchSetPreferences, setSetPreference, setCollectionItemCondition,
} from "../lib/api";
import { getCollectionKey } from "../lib/collection";
import { readJson, writeJson } from "../lib/offlineStore";
import { queueWrites, readQueue, removeFromQueue, queueSize } from "../lib/offlineQueue";
import { useNetworkStatus } from "./useNetworkStatus";

const DEFAULT_CONDITION = "Near Mint";

// Patche le cache hors ligne de LECTURE (collection-status.json) en même
// temps que la file d'écriture : sans ça, fermer l'appli et la rouvrir en
// restant hors ligne ferait disparaître le changement (l'affichage
// reviendrait à la dernière synchro réussie). patches: { [key]: { status,
// condition } | null } (null = suppression), fusionnées en un seul
// aller-retour disque même pour une opération en masse.
async function patchLocalCollectionCache(patches) {
  const cached = (await readJson("collection-status.json")) || { collection: {}, conditions: {} };
  const nextCollection = { ...cached.collection };
  const nextConditions = { ...cached.conditions };
  Object.entries(patches).forEach(([key, patch]) => {
    if (patch === null) {
      delete nextCollection[key];
      delete nextConditions[key];
    } else {
      nextCollection[key] = patch.status;
      if (patch.condition) nextConditions[key] = patch.condition;
    }
  });
  await writeJson("collection-status.json", { collection: nextCollection, conditions: nextConditions });
}

export function useCollection() {
  const [collection, setCollection] = useState({});
  const [conditions, setConditions] = useState({});
  const [stats, setStats] = useState({
    owned_unique: 0, owned_total: 0, wanted_count: 0, estimated_value: 0,
  });
  const [completion, setCompletion] = useState([]);
  const [setPreferences, setSetPreferencesMap] = useState({});
  const [pendingWrites, setPendingWrites] = useState(0);
  const [syncingWrites, setSyncingWrites] = useState(false);
  const online = useNetworkStatus();

  const reloadCollection = useCallback(async () => {
    try {
      const { collection: coll, conditions: cond } = await fetchCollectionCardIds();
      setCollection(coll);
      setConditions(cond);
    } catch (err) { console.error(err); }
  }, []);

  const reloadStats = useCallback(async () => {
    try { setStats(await fetchCollectionStats()); }
    catch (err) { console.error(err); }
  }, []);

  const reloadCompletion = useCallback(async () => {
    try { setCompletion(await fetchCollectionCompletion()); }
    catch (err) { console.error(err); }
  }, []);

  const reloadSetPreferences = useCallback(async () => {
    try { setSetPreferencesMap(await fetchSetPreferences()); }
    catch (err) { console.error(err); }
  }, []);

  const refreshPendingCount = useCallback(async () => {
    setPendingWrites(await queueSize());
  }, []);

  useEffect(() => {
    reloadCollection();
    reloadStats();
    reloadCompletion();
    reloadSetPreferences();
    refreshPendingCount();
  }, [reloadCollection, reloadStats, reloadCompletion, reloadSetPreferences, refreshPendingCount]);

  // Un WebView Capacitor backgrondé puis rappelé au premier plan REPREND son
  // contexte JS tel quel — il ne recharge PAS la page. Sans ça, `collection`
  // (utilisée par SetDetailView pour calculer sa propre complétion en local,
  // cf. commentaire de setStats) reste figée sur l'état du dernier lancement
  // à froid, même après avoir ajouté des dizaines de cartes ailleurs (site
  // web, ou une session précédente de l'appli) : l'accueil peut alors afficher
  // un pourcentage juste (recalculé serveur) pendant qu'un set ouvert affiche
  // un pourcentage calculé sur cette collection périmée. No-op sur le web
  // (même esprit que useAndroidBackButton.js).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listener;
    let cancelled = false;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) return;
      reloadCollection();
      reloadStats();
      reloadCompletion();
      reloadSetPreferences();
    }).then(h => { if (cancelled) h.remove(); else listener = h; });
    return () => { cancelled = true; listener?.remove(); };
  }, [reloadCollection, reloadStats, reloadCompletion, reloadSetPreferences]);

  // Rejoue la file d'écritures en attente : appelée au montage (si déjà en
  // ligne, pour une file laissée par une session précédente) et à chaque
  // retour de connexion (cf. l'effet plus bas). Rejeu par entrée, dans
  // l'ordre — un vrai rejet serveur abandonne l'entrée (pas de boucle
  // infinie sur un cas structurellement invalide), une nouvelle coupure
  // interrompt le rejeu et garde le reste pour la prochaine tentative.
  const flushQueue = useCallback(async () => {
    const queue = await readQueue();
    const keys = Object.keys(queue);
    if (keys.length === 0) return;
    setSyncingWrites(true);
    const done = [];
    for (const key of keys) {
      const op = queue[key];
      try {
        if (op.type === "status") await addToCollection(op.cardId, op.variant, op.status);
        else if (op.type === "remove") await removeFromCollection(op.cardId, op.variant);
        done.push(key);
      } catch (err) {
        if (err?.response) {
          console.error("Écriture hors ligne rejetée par le serveur", op, err);
          done.push(key);
          continue;
        }
        break;
      }
    }
    if (done.length) await removeFromQueue(done);
    setSyncingWrites(false);
    refreshPendingCount();
    reloadCollection(); reloadStats(); reloadCompletion();
  }, [refreshPendingCount, reloadCollection, reloadStats, reloadCompletion]);

  useEffect(() => { if (online) flushQueue(); }, [online, flushQueue]);

  // Bascule Set normal / Master Set pour un set : la complétion (%) en
  // dépend, donc on recharge aussi reloadCompletion après le changement.
  const updateSetMode = useCallback(async (setId, mode) => {
    try {
      await setSetPreference(setId, mode);
      setSetPreferencesMap(prev => {
        const next = { ...prev };
        // "master" ET "masterplus" sont des préférences à conserver ; seul
        // "normal" (défaut) supprime l'entrée. Oublier "masterplus" ici
        // faisait retomber le bouton et les stats en normal juste après le clic.
        if (mode === "master" || mode === "masterplus") next[setId] = mode;
        else delete next[setId];
        return next;
      });
      reloadCompletion();
    } catch (err) { console.error(err); }
  }, [reloadCompletion]);

  const setStatus = useCallback(async (cardId, variant, status) => {
    const key = getCollectionKey(cardId, variant);
    // Optimiste dans TOUS les cas (pas seulement après succès réseau,
    // contrairement à avant) : indispensable hors ligne puisqu'il n'y aura
    // justement pas de réponse tout de suite, et sans effet perceptible en
    // ligne (le aller-retour réseau prenait de toute façon quelques
    // dizaines de ms après cette mise à jour).
    setCollection(prev => ({ ...prev, [key]: status }));
    const nextCondition = status === "owned" ? (conditions[key] || DEFAULT_CONDITION) : null;
    if (status === "owned") {
      setConditions(prev => (prev[key] ? prev : { ...prev, [key]: DEFAULT_CONDITION }));
    }
    try {
      await addToCollection(cardId, variant, status);
      reloadStats(); reloadCompletion();
    } catch (err) {
      if (err?.response) {
        // Vrai rejet serveur : annuler l'optimisme en resynchronisant
        // depuis la vérité serveur plutôt que de laisser un état inventé.
        console.error(err);
        reloadCollection();
        return;
      }
      // Pas de réponse du tout = coupure réseau : mettre en file, patcher
      // le cache local de lecture, garder l'état optimiste affiché.
      await queueWrites({ [key]: { type: "status", cardId, variant, status } });
      await patchLocalCollectionCache({ [key]: { status, condition: nextCondition } });
      refreshPendingCount();
    }
  }, [conditions, reloadStats, reloadCompletion, reloadCollection, refreshPendingCount]);

  const removeStatus = useCallback(async (cardId, variant) => {
    const key = getCollectionKey(cardId, variant);
    setCollection(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setConditions(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    try {
      await removeFromCollection(cardId, variant);
      reloadStats(); reloadCompletion();
    } catch (err) {
      if (err?.response) {
        console.error(err);
        reloadCollection();
        return;
      }
      await queueWrites({ [key]: { type: "remove", cardId, variant } });
      await patchLocalCollectionCache({ [key]: null });
      refreshPendingCount();
    }
  }, [reloadStats, reloadCompletion, reloadCollection, refreshPendingCount]);

  const updateCondition = useCallback(async (cardId, variant, condition) => {
    const key = getCollectionKey(cardId, variant);
    try {
      await setCollectionItemCondition(cardId, variant, condition);
      setConditions(prev => {
        const next = { ...prev };
        if (condition) next[key] = condition;
        else delete next[key];
        return next;
      });
    } catch (err) { console.error(err); }
  }, []);

  // Actions en masse : même logique que setStatus/removeStatus mais sur une
  // liste de {card_id, variant} en un seul appel réseau, puis un seul reload.
  const bulkMarkStatus = useCallback(async (items, status) => {
    if (items.length === 0) return;
    setCollection(prev => {
      const next = { ...prev };
      items.forEach(({ card_id, variant }) => {
        next[getCollectionKey(card_id, variant)] = status;
      });
      return next;
    });
    if (status === "owned") {
      setConditions(prev => {
        const next = { ...prev };
        items.forEach(({ card_id, variant }) => {
          const key = getCollectionKey(card_id, variant);
          if (!next[key]) next[key] = DEFAULT_CONDITION;
        });
        return next;
      });
    }
    try {
      await bulkSetStatus(items, status);
      reloadStats(); reloadCompletion();
    } catch (err) {
      if (err?.response) {
        console.error(err);
        reloadCollection();
        return;
      }
      const entries = {};
      const patches = {};
      items.forEach(({ card_id, variant }) => {
        const key = getCollectionKey(card_id, variant);
        entries[key] = { type: "status", cardId: card_id, variant, status };
        patches[key] = { status, condition: status === "owned" ? (conditions[key] || DEFAULT_CONDITION) : null };
      });
      await queueWrites(entries);
      await patchLocalCollectionCache(patches);
      refreshPendingCount();
    }
  }, [conditions, reloadStats, reloadCompletion, reloadCollection, refreshPendingCount]);

  const bulkRemoveStatus = useCallback(async (items) => {
    if (items.length === 0) return;
    setCollection(prev => {
      const next = { ...prev };
      items.forEach(({ card_id, variant }) => {
        delete next[getCollectionKey(card_id, variant)];
      });
      return next;
    });
    setConditions(prev => {
      const next = { ...prev };
      items.forEach(({ card_id, variant }) => {
        delete next[getCollectionKey(card_id, variant)];
      });
      return next;
    });
    try {
      await bulkRemoveFromCollection(items);
      reloadStats(); reloadCompletion();
    } catch (err) {
      if (err?.response) {
        console.error(err);
        reloadCollection();
        return;
      }
      const entries = {};
      const patches = {};
      items.forEach(({ card_id, variant }) => {
        const key = getCollectionKey(card_id, variant);
        entries[key] = { type: "remove", cardId: card_id, variant };
        patches[key] = null;
      });
      await queueWrites(entries);
      await patchLocalCollectionCache(patches);
      refreshPendingCount();
    }
  }, [reloadStats, reloadCompletion, reloadCollection, refreshPendingCount]);

  // Map set_id → pourcentage de complétion
  const completionMap = useMemo(() => {
    const map = {};
    completion.forEach(c => { map[c.set_id] = c.completion_percentage; });
    return map;
  }, [completion]);
  // Map set_id → { owned, total } (barres de progression de la barre latérale)
  const completionCounts = useMemo(() => {
    const map = {};
    completion.forEach(c => { map[c.set_id] = { owned: c.owned_cards, total: c.total_cards }; });
    return map;
  }, [completion]);

  return {
    collection, conditions, stats, completion, completionMap, completionCounts, setPreferences,
    setStatus, removeStatus, bulkMarkStatus, bulkRemoveStatus, updateSetMode,
    updateCondition, pendingWrites, syncingWrites, flushQueue, reloadCompletion,
  };
}
